<template>
  <div v-if="serverData" class="col-12 col-lg-6 mb-4 p-2 p-md-3">
    <v-card class="analytics-card shadow rounded-md d-flex flex-column">
      <v-card-text class="pa-6 d-flex flex-column">
        <div class="d-flex justify-space-between align-start mb-6">
          <div class="flex-grow-1">
            <div class="d-flex align-center mb-2">
              <div class="icon-wrapper bg-light-primary rounded mr-3">
                <v-icon size="24" style="color: #7322b0"
                  >mdi-package-variant-closed
                </v-icon>
              </div>
              <h3 class="font-weight-bold text-dark-75 mb-0">
                {{ $t("customer_order_analytics") }}
              </h3>
            </div>
            <p class="text-muted mb-0 ml-11 font-size-sm">
              {{ $t("customer_order_analytics_description") }}
            </p>
          </div>
          <v-btn
            icon
            small
            class="elevation-0"
            style="color: #7322b0"
            @click="exportChart(serverData.print_url)"
          >
            <v-icon size="20">mdi-download</v-icon>
          </v-btn>
        </div>

        <div class="summary-section pt-6">
          <v-row class="ma-0 summary-grid" dense>
            <v-col cols="12" sm="6" class="pa-2 pa-sm-3">
              <div class="summary-card-gradient text-center py-4 rounded">
                <div class="d-flex align-center justify-center mb-3">
                  <v-icon size="20" class="mr-2" style="color: #7322b0"
                    >mdi-account-group
                  </v-icon>
                  <p class="font-size-sm font-weight-medium text-dark-50 mb-0">
                    {{ $t("number_of_customers") }}
                  </p>
                </div>
                <div class="stat-badge-primary">
                  <h2
                    class="font-weight-bold mb-0 font-size-h6 font-size-md-h5"
                    style="color: #7322b0"
                  >
                    {{ serverData.total_customers }}
                  </h2>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" class="pa-2 pa-sm-3">
              <div
                class="summary-card-gradient text-center py-2 py-md-3 rounded"
              >
                <div class="d-flex align-center justify-center mb-3">
                  <v-icon size="20" class="mr-2" style="color: #c32c80"
                    >mdi-package-variant
                  </v-icon>
                  <p class="font-size-sm font-weight-medium text-dark-50 mb-0">
                    {{ $t("total_orders") }}
                  </p>
                </div>
                <div class="stat-badge-secondary">
                  <h2 class="font-weight-bold mb-0" style="color: #c32c80">
                    {{ serverData.total }}
                  </h2>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <div
          class="table-container overflow-auto adaptive-table-height d-none d-md-block"
        >
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr class="table-header">
                  <th class="text-left font-weight-bold text-dark-75">
                    {{ $t("customer") }}
                  </th>
                  <th class="text-center font-weight-bold text-dark-75">
                    {{ $t("total_orders") }}
                  </th>
                  <th class="text-right font-weight-bold text-dark-75">
                    {{ $t("action") }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(data, index) in serverData.values"
                  :key="index"
                  class="table-row clickable-row"
                  :class="{ 'row-hover': true }"
                  @click="openInsight(data)"
                >
                  <td
                    class="font-size-base font-weight-medium text-dark-75 py-3"
                  >
                    <client-column :value="data.customer"
                      >{{ data.customer }}
                    </client-column>
                  </td>
                  <td class="text-center py-3">
                    <span class="order-count-badge">
                      {{ data.total }}
                    </span>
                  </td>
                  <td class="text-right py-3">
                    <v-btn
                      @click.stop="openInsight(data)"
                      icon
                      small
                      class="insight-btn"
                      style="color: #7322b0"
                    >
                      <v-icon size="20">mdi-chart-line</v-icon>
                    </v-btn>
                    <v-icon size="16" class="ml-2" style="color: #7322b0"
                      >mdi-chevron-right
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <div class="mobile-card-list d-md-none">
          <div
            v-for="(data, index) in serverData.values"
            :key="'mobile-' + index"
            class="mobile-row"
            @click="openInsight(data)"
          >
            <div class="mobile-row-customer">
              <client-column :value="data.customer" />
            </div>
            <div class="mobile-row-meta">
              <span class="order-count-badge">{{ data.total }}</span>
              <v-icon size="20" style="color: #7322b0">mdi-chart-line</v-icon>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="inSightdialog"
      elevation-0
      persistent
      max-width="800px"
      v-if="inSightdialog"
    >
      <div class="bg-white poppins">
        <div class="modal-header py-4 align-center">
          <h4 class="mb-0 font-weight-bolder text-capitalize">
            {{ $t("orders_by_countries_for", { client: activeClient }) }}
          </h4>
          <div
            class="btn btn-sm btn-icon btn-active-light-info"
            @click="inSightdialog = false"
          >
            <span class="svg-icon">
              <v-icon size="22">mdi-close</v-icon>
            </span>
          </div>
        </div>
      </div>
      <div>
        <v-card elevation="0" class="px-1 py-2">
          <apexchart
            type="bar"
            height="300"
            :options="chartOptions"
            :series="[
              {
                name: $t('series'),
                data: values,
              },
            ]"
          ></apexchart>
        </v-card>
      </div>
    </v-dialog>
  </div>
  <div class="col-12 col-lg-6 mb-4" v-else>
    <v-skeleton-loader type="card"></v-skeleton-loader>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import ClientColumn from "@/own/components/datatable/ClientColumn.vue";

export default {
  name: "AnalyticsOrdersNew",
  props: ["item", "filterData", "exportChart"],
  components: {
    ClientColumn,
  },
  data: () => ({
    serverData: null,
    slides: ["First", "Second", "Third", "Fourth", "Fifth"],
    colors: [
      "green",
      "secondary",
      "yellow darken-4",
      "red lighten-2",
      "orange darken-1",
    ],
    clients: [],
    activeClient: null,
    bgColors: [
      "bg-light-success text-success",
      "bg-light-danger text-danger",
      "bg-light-info text-info",
      "bg-light-primary text-primary",
      "bg-light-warning text-warning",
      "bg-light-dark text-dark",
    ],
    inSightdialog: false,
  }),
  beforeMount() {
    this.loadDataFromServer();
  },
  methods: {
    loadDataFromServer() {
      let data = this.filterData;
      ApiService.post(`${this.item.url}`, data)
        .then((response) => {
          this.serverData = response.data.chart;
          this.clients = this.serverData.values.map((item) => ({
            name: item.customer.name,
            total: item.total,
            class: this.getRandomColors(),
          }));

          this.activeClient = this.clients[0].name;
        })
        .catch(() => {});
    },
    getRandomColors: function () {
      return this.bgColors[Math.floor(Math.random() * this.bgColors.length)];
    },
    setActiveClient(client) {
      this.activeClient = client.name;
    },
    openInsight(data) {
      this.inSightdialog = true;
      this.activeClient = data.customer.name;
      // console.log(data);
    },
  },
  computed: {
    chartOptions: function () {
      return {
        chart: {
          type: "bar",
          toolbar: {
            show: true,
          },
          events: {
            // dataPointSelection: function (event, chartContext, config) {
            //   console.log(event);
            //   console.log(chartContext);
            //   console.log(config);
            // },
            // seriesIndex: function (event, chartContext, config) {
            //   console.log(event);
            //   console.log(chartContext);
            //   console.log(config);
            // },
          },
        },

        fill: {
          colors: ["#0093f5"],
          opacity: 1,
        },
        plotOptions: {
          bar: {
            borderRadius: 7,
            columnWidth: "25%",
            dataLabels: {
              position: "top", // top, center, bottom// offset by 20px
            },
          },
        },
        grid: {
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
          borderColor: "#b0b4c2",
          strokeDashArray: 4,
          position: "back",
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val;
          },
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#304758"],
          },
        },

        xaxis: {
          labels: {
            show: true,
            style: {
              colors: "#b1b5c3",
              fontSize: "12px",
            },
          },
          axisBorder: {
            show: false,
          },
          position: "bottom",
          tooltip: {
            enabled: false,
          },
          categories: this.countries,
        },

        yaxis: {
          axisBorder: {
            show: false,
          },
          labels: {
            show: true,
            formatter: function (val) {
              return val + "";
            },
            style: {
              colors: ["#afb2c1"],
              fontSize: "12px",
              fontWeight: 500,
            },
          },
        },

        title: {
          floating: true,
          offsetY: 330,
          align: "center",
          style: {
            color: "#444",
          },
        },

        tooltip: {
          enabled: false,
        },
      };
    },
    countries: function () {
      return (
        this.serverData?.values
          ?.find((item) => item.customer.name === this.activeClient)
          ?.countries.map((item) => item.country_name) || []
      );
    },
    values: function () {
      return (
        this.serverData?.values
          ?.find((item) => item.customer.name === this.activeClient)
          ?.countries.map((item) => item.amount) || []
      );
    },
  },

  watch: {
    serverData: {
      handler(newValue) {
        if (newValue) {
          // this.activeClient = newValue.values[0].customer;
        }
      },
      immediate: true,
    },
    filterData() {
      this.serverData = null;
      this.loadDataFromServer();
    },
  },
};
</script>

<style lang="scss">
.active-client {
  background-color: #e1d4f2;
  border-radius: 18px;
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-light-primary {
  background-color: rgba(115, 34, 176, 0.1);
}

.text-dark-50 {
  color: rgba(0, 0, 0, 0.5);
}

.text-dark-75 {
  color: rgba(0, 0, 0, 0.75);
}

.text-muted {
  color: #6c757d;
}

.modern-table {
  thead tr.table-header {
    background-color: #f8f9fa;
    border-bottom: 2px solid #e9ecef;

    th {
      padding: 12px 16px;
      font-size: 0.875rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border-bottom: none !important;
    }
  }

  tbody tr.table-row {
    border-bottom: 1px solid #f1f3f5;
    transition: all 0.2s ease;

    &.clickable-row {
      cursor: pointer;

      &:hover {
        background-color: rgba(115, 34, 176, 0.05);
        border-left: 3px solid #7322b0;
      }
    }

    &:hover {
      background-color: #f8f9fa;
    }

    td {
      border-bottom: none !important;
    }
  }
}

.insight-btn {
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.summary-section {
  border-top: 2px solid #f1f3f5;

  .summary-card-gradient {
    background: linear-gradient(
      135deg,
      rgba(115, 34, 176, 0.05) 0%,
      rgba(195, 44, 128, 0.05) 100%
    );
    border: 1.5px solid rgba(115, 34, 176, 0.15);
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(115, 34, 176, 0.1) 0%,
        rgba(195, 44, 128, 0.1) 100%
      );
      border-color: rgba(115, 34, 176, 0.3);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(115, 34, 176, 0.15);
    }
  }

  .stat-badge-primary {
    display: inline-block;
    padding: 8px 24px;
    background: linear-gradient(
      135deg,
      rgba(115, 34, 176, 0.15) 0%,
      rgba(115, 34, 176, 0.1) 100%
    );
    border: 1.5px solid rgba(115, 34, 176, 0.3);
    border-radius: 24px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(115, 34, 176, 0.25) 0%,
        rgba(115, 34, 176, 0.2) 100%
      );
      border-color: rgba(115, 34, 176, 0.5);
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(115, 34, 176, 0.2);
    }
  }

  .stat-badge-secondary {
    display: inline-block;
    padding: 8px 24px;
    background: linear-gradient(
      135deg,
      rgba(195, 44, 128, 0.15) 0%,
      rgba(195, 44, 128, 0.1) 100%
    );
    border: 1.5px solid rgba(195, 44, 128, 0.3);
    border-radius: 24px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(195, 44, 128, 0.25) 0%,
        rgba(195, 44, 128, 0.2) 100%
      );
      border-color: rgba(195, 44, 128, 0.5);
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(195, 44, 128, 0.2);
    }
  }
}

.analytics-card {
  min-height: auto;
}

.summary-grid .v-col {
  display: flex;
}

.adaptive-table-height {
  max-height: 35vh;

  @media (min-width: 1440px) {
    max-height: 400px;
  }

  @media (min-width: 1920px) {
    max-height: 450px;
  }

  @media (max-width: 767px) {
    max-height: none;
  }
}

.table-container {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f3f5;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;

    &:hover {
      background: #a0aec0;
    }
  }
}

.mobile-card-list {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-card-list .mobile-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  padding: 10px 4px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 0;
}

.mobile-card-list .mobile-row:hover {
  background-color: rgba(115, 34, 176, 0.05);
}

.mobile-card-list .mobile-row-customer {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.mobile-card-list .mobile-row-customer > div {
  display: flex !important;
  align-items: center;
  min-width: 0;
  width: 100%;
  padding-inline-start: 0 !important;
}

.mobile-card-list .mobile-row-customer .first-text {
  display: block !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
  max-width: none !important;
}

.mobile-card-list .mobile-row-meta {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 767px) {
  .analytics-card .v-card-text {
    padding: 1rem !important;
  }

  .summary-section {
    padding-top: 1.25rem;
  }

  .summary-grid .v-col {
    padding-inline-start: 0 !important;
    padding-inline-end: 0 !important;
  }
}

.order-count-badge {
  display: inline-block;
  padding: 4px 12px;
  background: rgba(115, 34, 176, 0.1);
  color: #7322b0;
  font-weight: 600;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid rgba(115, 34, 176, 0.2);
}
</style>
